import Input from '@components/Input';
import type { Comparator } from '@domain/types/filters';

import { FIELD_DEF_MAP } from '../constants';
import type { FieldDef, FilterRow, Option, OptionsRecord } from '../types';

import { DateInput } from './DateInput';
import { EnumMultiSelect } from './EnumMultiSelect';
import { FlagSelect } from './FlagSelect';
import { NumberInput } from './NumberInput';
import { TextInput } from './TextInput';

interface ValueInputProps {
  field: string;
  comparator: Comparator | '';
  values: string[];
  optionsRecord: OptionsRecord;
  datapointFieldDef?: FieldDef;
  onUpdate: (patch: Partial<FilterRow>) => void;
}

const disabledProps = {
  placeholder: 'Set a value',
  isDisabled: true,
  isReadOnly: true,
  className: 'bg-white dark:bg-white',
} as const;

export function ValueInput({
  field,
  comparator,
  values,
  optionsRecord,
  datapointFieldDef,
  onUpdate,
}: Readonly<ValueInputProps>) {
  if (!field) {
    return <Input {...disabledProps} />;
  }

  const fieldDef = FIELD_DEF_MAP.get(field) ?? datapointFieldDef;
  const kind = fieldDef?.valueInput;
  const options: Option[] = fieldDef?.options ?? optionsRecord[field] ?? [];

  // FlagSelect sets its own comparator — do not require one to be pre-set.
  if (kind === 'FlagSelect') {
    const flagMode = fieldDef?.comparators.includes('ex') ? 'exists' : 'count';
    return (
      <FlagSelect
        comparator={comparator}
        values={values}
        mode={flagMode}
        options={fieldDef?.flagOptions}
        onUpdate={onUpdate}
      />
    );
  }

  // All other input types require a comparator to be selected first.
  if (!comparator) {
    return <Input {...disabledProps} />;
  }

  switch (kind) {
    case 'EnumMultiSelect':
    case 'LookupMultiSelect':
      return (
        <EnumMultiSelect
          key={field}
          dataTestId={`query-builder-value:${field}`}
          options={options}
          values={values}
          onChange={(v) => onUpdate({ values: v })}
        />
      );

    case 'NumberInput':
      return <NumberInput values={values} onUpdate={(v) => onUpdate({ values: v })} format={fieldDef?.numberFormat} />;

    case 'DateInput':
      return <DateInput values={values} onChange={(v) => onUpdate({ values: v })} />;

    case 'TextInput':
    default:
      return <TextInput values={values} onUpdate={(v) => onUpdate({ values: v })} />;
  }
}
